
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Checkbox from "primevue/checkbox";
import {defineComponent, onMounted, PropType, reactive, ref} from "vue";
import {DocumentTableColumn, XmlColumnFieldTraining} from "@dex/squeeze-client-ts";
import {DocumentClass} from "@dex/squeeze-client-ts";
import Tooltip from "primevue/tooltip";
import {useI18n} from "vue-i18n";
import {useToast} from "primevue/usetoast";
import useSqueezeStore from "@/apps/squeeze/store";
import {ClientManager} from "@/singletons/ClientManager";
import {useRoute} from "vue-router";
import {TableListSettings, TableSettings} from "@/util/TableSettings";
import {DocumentFilterObject} from "@/apps/squeeze/interfaces/DocumentSearch";
import {FilterMatchMode, FilterService} from "primevue/api";
import InputText from "primevue/inputtext";
import {showDropdownOverlay} from "@/util/StylesHelper";
import Dropdown from "primevue/dropdown";
import {copyToClipboard, downloadData} from "@/util/Export";

export default defineComponent({
	name: "XmlMapperPositionList",
	methods: {copyToClipboard, downloadData},
	components: {
		DataTable, Column, Checkbox, InputText, Dropdown,
	},
	props: {
		mappingColumns: {
			type: Array as PropType<XmlColumnFieldTraining[]>,
			default: () => [],
		},
		allColumnsOfTableLineItems: {
			type: Array as PropType<DocumentTableColumn[]>,
			default: () => [],
		},
		loading: {
			type: Boolean,
			default: false,
		},
		documentClasses: {
			type: Array as PropType<DocumentClass[]>,
			default: () => [],
		},
	},
	directives: {
		'tooltip': Tooltip,
	},
	emits: [
		"onClickDelete", "onEntrySelect", "openTraining", "openTesting", "changeShowGlobalMapping", "changeActiveStatus",
	],
	setup(props, {emit}) {
		const {t} = useI18n();
		const toast = useToast();
		const route = useRoute();

		/** Vuex Store */
		const store = useSqueezeStore();

		/** User API endpoint */
		const userApi = ClientManager.getInstance().squeeze.user;

		/** Current table list pagination */
		const tableListPagination = reactive<TableListSettings>({});

		/** Should the clear filter button be shown in the table? */
		const showBtnClearFilter = ref<boolean>(false);

		/** Filters of list (Currently not activated) */
		const filters = ref<DocumentFilterObject>({});

		/** Sets the default sorting for the table (currently deactivated) */
		const multiSortMeta = ref<any[]>([{field: "company", order: -1}, {field: "creditor", order: -1}, {field: "documentclassid", order: -1}, {field: "readonly", order: 1}]);

		/** Should the button for hiding global Mappings be shown? */
		const showGlobalEntries = ref<boolean>(true);

		/**
		 * Custom Filter for Document-Class. Needed because of different types in table and documentclass-data
		 */
		FilterService.register('customWildCardFilter', (rowValue, selectedValue) => {
			if (String(rowValue) === "*") {
				return true;
			}

			if (!selectedValue) {
				return true;
			}
			return rowValue.includes(selectedValue);
		});


		/**
		 * Custom Filter for Document-Class. Needed because of different types in table and documentclass-data
		 */
		FilterService.register('customFilterDocumentClass', (rowValue, selectedValue) => {
			if (String(rowValue) === "*") {
				return true;
			}

			if (!selectedValue) {
				return true;
			}

			if (String(rowValue) === String(selectedValue)) {
				return true;
			}

			return false;
		});


		/** Init filters */
		const initFilters = () => {
			filters.value = {
				"company": {value: null, matchMode: "customWildCardFilter"},
				"creditor": {value: null, matchMode: "customWildCardFilter"},
				"documentclassid": {value: null, matchMode: "customFilterDocumentClass"},
				"columnName": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"xpath": {value: null, matchMode: FilterMatchMode.CONTAINS},
			};
		}

		/**
		 * Opens the Delete-Dialog
		 * @param row The row to delete
		 */
		const openDeleteDialog = (row: XmlColumnFieldTraining) => {
			emit("onClickDelete", row);
		}

		/**
		 * Opens the Edit-Dialog
		 * @param {BatchClass} data Data to edit
		 */
		const openEditDialog = (data: XmlColumnFieldTraining) => {
			/**
			 * The model for XmlColumnFieldTraining expects a string for the document class ID,
			 * but the document class ID is normally a number
			 */
			if (data.documentclassid === "*") {
				data.documentclassid = 0 as any;
			} else {
				data.documentclassid = Number(data.documentclassid) as any;
			}
			emit("onEntrySelect", data, true)
		}

		/** Triggered when the New Entry Button is clicked */
		const onNewEntryClick = () => {
			emit("onEntrySelect", {columnName: '', documentclassid: 0, creditor: '', readonly: false, xpath: '/', company: '', id: undefined})
		}

		/**
		 * Get the description of a column
		 * @param row
		 */
		const getColumnDescription = (row: XmlColumnFieldTraining) => {
			if (props.allColumnsOfTableLineItems) {
				const columns = props.allColumnsOfTableLineItems.filter(column => column.name === row.columnName);

				if (columns.length > 0) {
					return row.columnName + " (" + columns[0].description + ")";
				}
			}

			return row.columnName;
		}

		/**
		 * Get the description of a column
		 * @param row
		 */
		const getColumnDescriptionTooltip = (row: XmlColumnFieldTraining) => {
			let columnDescriptions = row.columnName;
			if (props.allColumnsOfTableLineItems) {
				const columns = props.allColumnsOfTableLineItems.filter(column => column.name === row.columnName);
				columnDescriptions = "";
				columns.forEach(column => {
					columnDescriptions += column.description! + "\n";
				})
			}

			return columnDescriptions;
		}

		/**
		 * Get the description of a column (for the dropdown)
		 * @param row
		 */
		const getColumnDescriptionDropDown = (row: any) => {
			if (props.allColumnsOfTableLineItems) {
				const column = props.allColumnsOfTableLineItems.find(column => column.name === row.name);

				if (column) {
					return row.name + " (" + column.description + ")";
				}
			}

			return row.name;
		}

		/** Get the description of a documentClass instead of number */
		const getDescription = (row: XmlColumnFieldTraining) => {
			if (String(row.documentclassid) === '0') {
				return  "*";
			}

			if (props.documentClasses) {
				const docClass = props.documentClasses.find(docClass => String(docClass.id) === String(row.documentclassid) ||
					(docClass.id === 0 && String(row.documentclassid) === '*'));
				if (docClass) {
					return docClass.description
				}
			}

			return row.documentclassid
		}

		onMounted(() => {
			tableListPagination.pageSize = TableSettings.getTableListPagination(store, route.name);
		});

		/** Triggered when page is changed */
		const onChangePage = (event: { page: number; rows: number }) => {
			tableListPagination.pageSize = event.rows;
			TableSettings.saveTableListPagination(t, toast, store, userApi, route.name, event.rows);
		}

		/**
		 * Triggered when the show global Button is clicked
		 * @param showGlobalMapping
		 */
		const onClickShowGlobal = (showGlobalMapping: boolean) => {
			showGlobalEntries.value = !showGlobalEntries.value;
			emit("changeShowGlobalMapping", showGlobalMapping);
		}

		initFilters();

		/**
		 * Triggered when a filter has been entered
		 * @param event
		 */
		const onFilter = (event: any) => {
			if (props.mappingColumns) {
				showBtnClearFilter.value = event.filteredValue.length < props.mappingColumns.length;
			}
		}

		/**
		 * Tries to create an printable array from an x-path string
		 * @param xPath
		 */
		const createPrintableXpath = (xPath: string) => {
			let indentation = 0;
			const parts = xPath.split('/').filter(Boolean);

			const xPaths = [] as any;

			parts.forEach((part, index) => {
				const xPathFormat = {
					indentation: 0,
					xPathPart: '',
				}

				// Most entries in positions don't have a slash at the beginning
				if (index === 0) {
					if (xPath[0]=== "/") {
						xPathFormat.xPathPart = "/" + part;
					} else {
						xPathFormat.xPathPart = part;
					}
				} else {
					xPathFormat.xPathPart = "/" + part;
				}

				xPathFormat.indentation = indentation;

				// Increase indentation for nested elements
				indentation += 1;
				xPaths.push(xPathFormat);
			});

			return xPaths;
		}

		/**
		 * Triggered when the active status of a xml training is clicked
		 * @param {XmlColumnFieldTraining} data Data to edit
		 */
		const onChangeActiveStatus = (data: XmlColumnFieldTraining) => {
			emit("changeActiveStatus", data);
		}

		return {
			store, tableListPagination, filters, multiSortMeta, showGlobalEntries,
			openDeleteDialog, onNewEntryClick, getDescription, onChangePage, openEditDialog,
			getColumnDescription, onFilter, initFilters, showDropdownOverlay,
			getColumnDescriptionDropDown, onClickShowGlobal, createPrintableXpath, getColumnDescriptionTooltip,
			onChangeActiveStatus,
		}
	},
});
